import {FloatingPortal} from '@/components/main/common';
import {ROUTE} from '@/constants/main/route';
import {useLocation} from 'react-router-dom';
import {useEffect, useMemo, useState} from 'react';
import {getCookie, setCookie, setLayerCookie} from '@/utils/main';
import SavygContestLayer from '@/components/layer-popup-bodies/main/SavygContestLayer';

/**
 * @typedef {Object} BodyComponentsType
 * @property {string} id - 팝업 고유 ID
 * @property {React.ComponentType} component - 팝업 컴포넌트
 * @property {string[]} path - 유효 노출 영역에 대한 경로값
 * @property {Object} props - 팝업 별 추가 프로퍼티
 * @property {Date} [startDate] - 노출 시작 시간
 * @property {Date} [endDate] - 노출 종료 시간 (추후 작업시 기한 지난 것은 수동으로 삭제 필요)
 */

/** SoopUiLayerPopup 구현을 위한 공용 훅 */
export const useSoopUiPopupLayer = (layerId, onClose) => {
  /** 오늘 하루 보지않기를 위한 상태값 */
  const [isCheckedToday, setIsCheckedToday] = useState(false);

  /**
   * 닫기 버튼 눌렀을 때
   */
  const handleClose = () => {
    if (isCheckedToday) {
      // 오늘 하루 보지않기 선택 후 닫기 할 경우 쿠키 설정
      setCookie(layerId, 'y', undefined);
    }
    onClose();
  };

  const handleLayerClose = ({expireDays = 1, expireAtMidnight = false}) => {
    if (isCheckedToday) {
      // 오늘 하루 보지않기 선택 후 닫기 할 경우 쿠키 설정
      setLayerCookie(layerId, 'y', undefined, expireDays, expireAtMidnight);
    }
    onClose();
  };

  return {
    isCheckedToday,
    setIsCheckedToday,
    handleClose,
    handleLayerClose,
  };
};

/**
 * 먼저 정의된 배너가 더 높은 우선순위를 갖습니다.
 * @type {BodyComponentsType[]}
 * @deprecated 어드민 관리로 바뀌었습니다.
 * @note https://admin.sooplive.co.kr/app/main/main_service_banner.php?szBannerType=PROMOTION
 */
const BodyComponents = [
  /* 예시 데이터 */
  // {
  //   id: 'main-popup-no-purchase-layer',
  //   component: NoPurchaseLayer,
  //   path: [ROUTE.INDEX],
  //   props: {},
  //   endDate: new Date('2025-04-09T00:00:00'),
  // },
  // {
  //   id: 'main-popup-subs-change-layer', // Note. 둘이 시간이 겹치기 때문에 id를 통해 24시간 동안 다시보지않기 공유하도록 설정
  //   component: SubsDirectChangeLayer,
  //   path: [ROUTE.INDEX],
  //   props: {},
  //   startDate: new Date('2025-10-26T12:00:00'),
  //   endDate: new Date('2025-10-26T18:00:00'),
  // },
  {
    id: 'main-popup-savyg-contest-layer',
    component: SavygContestLayer,
    path: [ROUTE.INDEX],
    props: {},
    startDate: new Date('2025-10-26T10:00:00'),
    endDate: new Date('2025-11-25T23:59:59'),
  },
];

/**
 * 화면 우하단 일부분을 차지하는 팝업 형태의 레이어 컴포넌트
 * @returns
 * @deprecated - 어드민 관리로 바뀌며 src/components/main/MyPlus/BottomBanner/index.tsx 로 대체되었습니다.
 */
const SoopUiLayerPopup = () => {
  const [isUserClosed, setIsUserClosed] = useState(false);
  const [layers, setLayers] = useState([]);
  const {pathname} = useLocation();

  const handleClose = (id) => {
    setIsUserClosed(true);
    setLayers((prev) => prev.filter((v) => v.id !== id));
  };

  useEffect(() => {
    setLayers([...BodyComponents]);
  }, [pathname]);

  /* 노출 조건 만족하는 배너 하나만 찾아서 노출 */
  const targetLayer = useMemo(() => {
    return layers.find(({id, path, startDate, endDate}, index) => {
      const isNotStarted = startDate ? new Date() <= startDate : false;
      const isExpired = endDate ? new Date() > endDate : false;
      const isHidden = getCookie(id) === 'y';
      const shouldShowLayer =
        path.includes(pathname) &&
        !isNotStarted &&
        !isExpired &&
        !isHidden &&
        !isUserClosed;

      return shouldShowLayer;
    });
  }, [layers, pathname, isUserClosed]);

  if (layers.length === 0) {
    return null;
  }

  return (
    <FloatingPortal
      className={'_Modal_UI_Wrap click _bnr'}
      target={'soop_wrap'}>
      <>
        {targetLayer && (
          <targetLayer.component
            onClose={() => handleClose(targetLayer.id)}
            key={targetLayer.id}
            layerId={targetLayer.id}
            {...targetLayer.props}
          />
        )}
      </>
    </FloatingPortal>
  );
};

export default SoopUiLayerPopup;
